// Gemeinsame Widget-Hülle des Dashboards (design_handoff_dashboard §3): Glas, Farbton-Ebene, Wasserzeichen-Icon,
// Leuchtrand, Schatten und schwebendes 3D-Icon – einmal hier statt in jedem Widget. Farben kommen als Token-Namen
// (RGB-Tripel aus .dash-flaeche, z. B. '--dw-orange').
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { CircleNotch, Info, type Icon } from '@phosphor-icons/react';

export type WidgetFarbe =
  | '--dw-orange' | '--dw-blau' | '--dw-tuerkis' | '--dw-mail' | '--dw-rot' | '--dw-termin'
  | '--dw-gruen' | '--dw-pink' | '--dw-graublau' | '--dw-lila' | '--dw-gold';

/** rgb(var(--x) / a) */
export const farbe = (f: WidgetFarbe, a = 1) => `rgb(var(${f}) / ${a})`;
const hell = (f: WidgetFarbe) => `color-mix(in srgb, rgb(var(${f})) 72%, #fff)`;

export interface DashboardWidgetProps {
  farbe: WidgetFarbe;
  /** Schwebendes 3D-Icon (fill) und Wasserzeichen (duotone) */
  icon: Icon;
  /** Ganze Karte klickbar (wie bisher per onClick); sonst nur leichtes Anheben beim Hover */
  onClick?: () => void;
  ariaLabel: string;
  tip?: string;
  spalte?: string;
  radius?: number;
  /** angedeutete Karten darunter (Tages-Briefing) */
  stapel?: boolean;
  /** 3D-Icon ausblenden (z. B. sehr schmale Karten) */
  ohneSchwebeIcon?: boolean;
  id?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export function DashboardWidget({ farbe: f, icon: I, onClick, ariaLabel, tip, spalte, radius = 24, stapel = false, ohneSchwebeIcon = false, id, style, children }: DashboardWidgetProps) {
  const klickbar = !!onClick;
  const taste = (e: KeyboardEvent<HTMLElement>) => {
    if (!onClick || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
  };
  return (
    <section id={id} aria-label={ariaLabel} data-tip={tip}
      role={klickbar ? 'link' : undefined} tabIndex={klickbar ? 0 : undefined}
      onClick={onClick} onKeyDown={klickbar ? taste : undefined}
      className={`dw ${klickbar ? 'dw-klick' : 'dw-still'} relative flex min-w-0 flex-col`}
      style={{ gridColumn: spalte, isolation: 'isolate', borderRadius: radius, padding: '16px 18px', gap: 12,
        background: 'var(--glass)', backdropFilter: 'blur(28px) saturate(170%)', WebkitBackdropFilter: 'blur(28px) saturate(170%)', boxShadow: 'var(--glass-sh)', ...style }}>
      {/* 2 Farbton + 3 Wasserzeichen */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: -1, borderRadius: 'inherit',
        background: `radial-gradient(110% 80% at 100% 0%, ${farbe(f, 0.34)}, ${farbe(f, 0)} 58%), radial-gradient(80% 60% at 0% 100%, ${farbe(f, 0.14)}, ${farbe(f, 0)} 70%), linear-gradient(180deg, ${farbe(f, 0.06)}, ${farbe(f, 0)})` }}>
        <span className="absolute" style={{ top: '-40%', left: '-30%', width: '60%', height: '180%', transform: 'rotate(25deg)',
          background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.06), rgba(255,255,255,0))' }} />
        <span className="absolute" style={{ right: -60, top: -60, width: 200, height: 200, borderRadius: '50%', filter: 'blur(10px)',
          background: `radial-gradient(circle, ${farbe(f, 0.35)}, ${farbe(f, 0)} 70%)` }} />
        <I weight="duotone" size={200} className="absolute" style={{ right: -30, bottom: -40, color: farbe(f), opacity: 0.16, transform: 'rotate(-14deg)' }} />
      </div>
      {/* 4 Leuchtrand */}
      <div aria-hidden className="dw-rand pointer-events-none absolute inset-0" style={{ zIndex: -1, borderRadius: 'inherit', padding: 1.2,
        background: `linear-gradient(150deg, ${farbe(f, 0.85)}, ${farbe(f, 0.15)} 30%, rgba(255,255,255,0) 55%, ${farbe(f, 0.3)})` }} />
      {/* 6 Schwebendes 3D-Icon – Unterkante 14 px unter der Kartenoberkante, Kopfzeile beginnt bei 16 px */}
      {!ohneSchwebeIcon && <SchwebeIcon farbe={f} icon={I} />}
      {stapel && (
        <>
          <div aria-hidden className="pointer-events-none absolute" style={{ left: 30, right: 30, top: '100%', height: 13, borderRadius: '0 0 18px 18px', background: 'var(--glass)', boxShadow: 'var(--glass-sh)', opacity: 0.45 }} />
          <div aria-hidden className="pointer-events-none absolute" style={{ left: 15, right: 15, top: '100%', height: 7, borderRadius: '0 0 20px 20px', background: 'var(--glass)', boxShadow: 'var(--glass-sh)', opacity: 0.85 }} />
        </>
      )}
      {children}
    </section>
  );
}

export function SchwebeIcon({ farbe: f, icon: I }: { farbe: WidgetFarbe; icon: Icon }) {
  return (
    <span aria-hidden className="dw-schwebe pointer-events-none absolute flex items-center justify-center overflow-hidden"
      style={{ top: -32, zIndex: 3, width: 46, height: 46, borderRadius: 14, transform: 'rotate(9deg)',
        background: `linear-gradient(155deg, ${hell(f)}, ${farbe(f)})`,
        boxShadow: `inset 0 2px 0 rgba(255,255,255,0.7), inset 0 -4px 8px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.18), 0 4px 8px rgba(0,0,0,0.22), 0 18px 30px -8px ${farbe(f, 0.7)}` }}>
      <span className="absolute inset-x-0 top-0" style={{ height: '50%', background: 'linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0))' }} />
      <I weight="fill" size={25} color="#fff" style={{ position: 'relative', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }} />
    </span>
  );
}

/** Farbige Icon-Kachel im Widget-Kopf (34 px, Glanz oben). */
export function KopfKachel({ farbe: f, icon: I, groesse = 34 }: { farbe: WidgetFarbe; icon: Icon; groesse?: number }) {
  return (
    <span className="relative flex shrink-0 items-center justify-center overflow-hidden"
      style={{ width: groesse, height: groesse, borderRadius: groesse * 0.3, color: '#fff',
        background: `linear-gradient(160deg, ${hell(f)}, ${farbe(f)})`,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 0 0 0.5px rgba(0,0,0,0.12), 0 8px 16px -8px rgba(0,0,0,0.45)' }}>
      <I weight="fill" size={groesse * 0.53} style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }} />
      <span className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(255,255,255,0.3), transparent 48%)' }} />
    </span>
  );
}

/** Widget-Kopf: Kachel, Titel 14/800, Untertitel 12/600 --ink3, rechts optional (z. B. Info-Icon mit Tooltip). */
export function WidgetKopf({ farbe: f, icon, titel, untertitel, rechts }: { farbe: WidgetFarbe; icon: Icon; titel: ReactNode; untertitel?: ReactNode; rechts?: ReactNode }) {
  return (
    <div className="flex items-center" style={{ gap: 10 }}>
      <KopfKachel farbe={f} icon={icon} />
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="m-0 truncate" style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{titel}</h3>
        {untertitel != null && <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>{untertitel}</span>}
      </div>
      {rechts}
    </div>
  );
}

/** Info-Icon im Widget-Kopf, trägt nur den Tooltip. */
export function KopfInfo({ tip }: { tip: string }) {
  return (
    <span data-tip={tip} aria-label={tip} role="img" tabIndex={0} className="flex shrink-0 rounded-full" style={{ color: 'var(--ink3)', cursor: 'help' }}>
      <Info size={18} />
    </span>
  );
}

/** Leerzustand eines Widgets: großes Icon + Text. */
export function WidgetLeer({ icon: I, text, farbeIcon = 'var(--ink3)' }: { icon: Icon; text: string; farbeIcon?: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center" style={{ padding: '22px 0', gap: 8, color: 'var(--ink3)' }}>
      <I weight="fill" size={30} style={{ color: farbeIcon }} />
      <span style={{ fontSize: 13.5, fontWeight: 700 }}>{text}</span>
    </div>
  );
}

/** Ladezustand eines Widgets. */
export function WidgetLaedt({ text }: { text?: string }) {
  return (
    <div className="flex flex-1 items-center justify-center" style={{ padding: '22px 0', gap: 8, color: 'var(--ink3)' }}>
      <CircleNotch size={22} className="animate-spin" />
      {text && <span style={{ fontSize: 13.5, fontWeight: 700 }}>{text}</span>}
    </div>
  );
}

export type KpiTon = 'blau' | 'rot' | 'gruen' | 'lila';

/** KPI-Karte (§3): farbiger Verlauf, gestapelte Karten dahinter, großes Wasserzeichen-Icon. */
export function KpiKarte({ ton, icon: I, label, wert, unter, tip, onClick, spalte, schmal }: {
  ton: KpiTon; icon: Icon; label: string; wert: ReactNode; unter: string; tip: string; onClick?: () => void; spalte?: string; schmal?: boolean;
}) {
  const bg = `var(--dw-kpi-${ton})`;
  return (
    <div className="relative" style={{ gridColumn: spalte, paddingBottom: 12 }}>
      <div aria-hidden className="absolute" style={{ left: 26, right: 26, bottom: 0, height: 40, borderRadius: 22, background: bg, opacity: 0.3 }} />
      <div aria-hidden className="absolute" style={{ left: 13, right: 13, bottom: 6, height: 40, borderRadius: 24, background: bg, opacity: 0.55 }} />
      <button type="button" onClick={onClick} data-tip={tip}
        className="dw dw-kpi relative flex h-full w-full flex-col justify-between overflow-hidden text-left"
        style={{ minHeight: 112, borderRadius: 26, padding: '14px 16px', gap: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: '#fff',
          background: bg, boxShadow: `inset 0 1px 1px rgba(255,255,255,0.35), 0 18px 34px -16px var(--dw-kpi-${ton}-glow)` }}>
        <I aria-hidden weight="fill" size={96} className="pointer-events-none absolute" style={{ right: -16, bottom: -26, opacity: 0.15, transform: 'rotate(-12deg)' }} />
        <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(255,255,255,0.22), transparent 45%)' }} />
        <span className="relative flex w-full items-center justify-between" style={{ gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.88)' }}>{label}</span>
          <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'rgba(255,255,255,0.2)',
            boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
            <I weight="fill" size={18} />
          </span>
        </span>
        <span className="relative flex flex-col" style={{ gap: 2 }}>
          <span className="tabular-nums" style={{ fontSize: schmal ? 20 : 22, fontWeight: 800, letterSpacing: '-0.03em' }}>{wert}</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{unter}</span>
        </span>
      </button>
    </div>
  );
}
